/**
 * mSTEM AI control panel modal container (Phase 7 / AI-01, DR-04).
 *
 * React-16 CLASS container (NO hooks; handlers bound via lodash.bindAll). Owns:
 *   - the get-user-media-promise camera lifecycle: handleEnableCamera starts a
 *     {video:true, audio:false} stream into a <video> ref on resolve (cameraOn=true),
 *     or keeps the dark placeholder + a denial caption on reject (cameraError=true).
 *     A second click toggles the camera OFF (stops the tracks). componentWillUnmount
 *     stops ALL tracks so the camera light is released — no leak (T-07-03-03).
 *   - the control-button mock-result flow: handleControlClick shows "Đang phân tích…"
 *     for MOCK_DELAY_MS, then renders the mock result from the ai-panel-results helper
 *     (which reads ai-results.js — the SAME single mock source the VM opcodes use; the
 *     panel never invokes the VM, RESEARCH Open Q2) + fires a success toast.
 *
 * Camera frames are NEVER captured/stored/transmitted (live <video> preview only) —
 * the threat-register mitigation for T-07-03-01.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {injectIntl} from 'react-intl';
import getUserMedia from 'get-user-media-promise';

import {closeAiPanel} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';
import {getPanelResult} from '../lib/mstem-mock/ai-panel-results';
import log from '../lib/log';

import AiPanelModalComponent from '../components/ai-panel-modal/ai-panel-modal.jsx';

// Mirrors the VM block delay (07-02 MOCK_DELAY_MS) so the panel demo and the
// workspace report bubbles feel the same.
const MOCK_DELAY_MS = 1500;

class AiPanelModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleEnableCamera',
            'handleControlClick',
            'handleClose',
            'setVideoRef'
        ]);
        this.state = {
            cameraOn: false,
            cameraError: false,
            analyzing: false,
            result: null
        };
        this.stream = null;
        this.videoRef = null;
        this._analyzeTimer = null;
    }
    componentWillUnmount () {
        // Release the camera (T-07-03-03 — stop all tracks, no leak).
        if (this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
        if (this._analyzeTimer !== null) {
            clearTimeout(this._analyzeTimer);
            this._analyzeTimer = null;
        }
    }
    setVideoRef (el) {
        this.videoRef = el;
        if (el && this.stream) {
            el.srcObject = this.stream;
        }
    }
    handleEnableCamera () {
        if (this.state.cameraOn) {
            // Toggle OFF — stop tracks, back to the placeholder.
            if (this.stream) {
                this.stream.getTracks().forEach(t => t.stop());
                this.stream = null;
            }
            if (this.videoRef) {
                this.videoRef.srcObject = null;
            }
            this.setState({cameraOn: false, cameraError: false});
            return;
        }
        getUserMedia({video: true, audio: false})
            .then(stream => {
                this.stream = stream;
                if (this.videoRef) {
                    this.videoRef.srcObject = stream;
                }
                this.setState({cameraOn: true, cameraError: false});
            })
            .catch(err => {
                log.warn(`[MBF AI] camera denied/unavailable: ${err && err.name}`);
                this.setState({cameraOn: false, cameraError: true});
            });
    }
    handleControlClick (controlId) {
        if (this._analyzeTimer !== null) {
            clearTimeout(this._analyzeTimer);
        }
        this.setState({analyzing: true, result: null});
        this._analyzeTimer = setTimeout(() => {
            this._analyzeTimer = null;
            this.setState({analyzing: false, result: getPanelResult(controlId)});
            this.props.onShowResultToast();
        }, MOCK_DELAY_MS);
    }
    handleClose () {
        this.props.onRequestClose();
    }
    render () {
        const {cameraOn, cameraError, analyzing, result} = this.state;
        return (
            <AiPanelModalComponent
                analyzing={analyzing}
                cameraError={cameraError}
                cameraOn={cameraOn}
                result={result}
                videoRefCallback={this.setVideoRef}
                onControlClick={this.handleControlClick}
                onEnableCamera={this.handleEnableCamera}
                onRequestClose={this.handleClose}
            />
        );
    }
}

AiPanelModal.propTypes = {
    onRequestClose: PropTypes.func,
    onShowResultToast: PropTypes.func
};

AiPanelModal.defaultProps = {
    onRequestClose: () => {},
    onShowResultToast: () => {}
};

const mapDispatchToProps = dispatch => ({
    onRequestClose: () => dispatch(closeAiPanel()),
    onShowResultToast: () => showAlertWithTimeout(dispatch, 'mstemAiResult')
});

export default injectIntl(connect(
    null,
    mapDispatchToProps
)(AiPanelModal));
